import qs from 'qs';
import { REDUX_ACTIONS } from 'utils/constants';
import { GetState, AppThunk, AppThunkP } from 'utils/frontendTypes';
import { toast } from 'react-toastify';
import { isUserThryveAdmin } from 'utils/index';
import { ResponseMsg } from 'utils/gloo-types';
import { isArray, isString } from 'utils/common';
import { getAuthCurrentUserIdToken } from 'utils/firebase';
import { v4 as uuidv4 } from 'uuid';
import { isRawFile } from 'utils/discover';
import { logoutAction } from './auth';
import { closeModal } from './modal';
import packageJson from '../../package.json';

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const { REACT_APP_ENABLE_SSO } = process.env;

const API_BASE_URL = baseUrl;
export const IS_LOCAL =
  baseUrl.startsWith('http://localhost') ||
  baseUrl.startsWith('http://192.168') ||
  baseUrl.startsWith('https://localhost');
const currentOrigin = window?.location?.origin || '';
export const IS_RCI_DEPLOYMENT = currentOrigin.includes('://rci.') || currentOrigin.includes('://rci.dev.');

export const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'App-Name': 'Dashboard',
  'App-Version': packageJson.version,
};

type ParamObject = { [key: string]: any };

function replaceUrlParam(url: string, pathParams?: ParamObject): string {
  if (url.includes('{') && url.includes('}') && pathParams) {
    let tempUrl: string = url;

    Object.entries(pathParams).forEach(([_key, value]) => {
      // Match the route/{routeId}
      let param: string = (tempUrl.match(/\{[A-Za-z0-9]*\}/) || [])[0];
      // Remove the brackets from the matching result
      if (param) {
        param = param.replace(/[{}]/g, '');
        if (typeof value !== 'undefined') {
          tempUrl = tempUrl.replace(`{${param}}`, value);
        }
      }
    });

    return tempUrl;
  }
  return url;
}

async function buildParams(options: RequestObj, getState: GetState) {
  const params = options.params || {};
  // Add on the version as a query parameter
  params.version = 'v1';
  if (options.anonymous) {
    return params;
  }

  // Users that are part of multiple orgs must pass up the org id that they are logged in to
  if (!params.orgId && (isUserThryveAdmin(getState().auth.orgUser) || getState().auth.accessOrgs?.length > 1)) {
    params.orgId = getState().auth.organization?.id;
  }

  if (REACT_APP_ENABLE_SSO === 'no') {
    if (params.access_token) {
      return params;
    }
    console.debug('Appending access token to params');
    const token = await getAuthCurrentUserIdToken();
    return { ...params, access_token: token };
  }

  return params;
}

export interface RequestObj {
  method: 'get' | 'post' | 'put' | 'delete';
  url: string;
  params?: ParamObject;
  pathParams?: ParamObject;
  data?: any;
  headers?: any;
  anonymous?: boolean;
  stringify?: boolean;
  blob?: boolean;
  showUnauthorizedToast?: boolean;
  // If an error message includes a value provided in this array, the toast will be silenced
  errorToastFilters?: string[];
}

const CLOUDINARY_FILE_CHUNK_SIZE = 20000000;
const CLOUDINARY_FILENAME_FORBIDDEN_CHARACTERS_REGEX = /^[\s/]|[?#&\\%<>]|[\s/]$/;

/*
 * The filename or "public_id" cannont contain ?#&\<> and cannot begin or end with a space or forward slash.
 * See Cloudinary docs: https://cloudinary.com/documentation/image_upload_api_reference#upload_optional_parameters
 */
const checkIfCloudinaryFileNameIsValid = (filename: string) => {
  if (CLOUDINARY_FILENAME_FORBIDDEN_CHARACTERS_REGEX.test(filename))
    throw new Error(
      `${filename} contains invalid characters. It cannot contain ?#&\\<> and cannot begin or end with a space or forward slash`,
    );
};

const uniqueFilename = (filename: string) => {
  const split = filename.split('.');
  const fileExt = split.pop();
  const fileName = split.join('.');
  const now = new Date().getTime();
  if (isRawFile(filename)) return `${fileName}_${now}.${fileExt}`;
  return `${fileName}_${now}`;
};

const handleCloudinaryLargeUpload = async (file, handleUpload, handleProgress) => {
  try {
    checkIfCloudinaryFileNameIsValid(file.name);
  } catch (err) {
    toast.error(`${err}`);
    console.error(err);
    return;
  }

  const fileId = uuidv4();
  const size = file.size;

  const getChunk = (start, end) => {
    const slice = file.mozSlice
      ? file.mozSlice
      : file.webkitSlice
        ? file.webkitSlice
        : file.slice
          ? file.slice
          : undefined;

    return slice.bind(file)(start, end);
  };

  const uploadChunk = async (chunk, start, end, filename) => {
    const url = `${process.env.REACT_APP_CLOUDINARY_API_URL}/upload`;
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('X-Unique-Upload-Id', fileId);
    xhr.setRequestHeader('Content-Range', `bytes ${start}-${end}/${size}`);

    if (handleProgress) {
      // progress can be used to show progress indicator in the ui
      xhr.upload.addEventListener('progress', (e) => {
        const totalProgress = (start + e.loaded) / size;
        handleProgress(Math.round(totalProgress * 100.0));
      });
    }

    xhr.onreadystatechange = async () => {
      if (xhr.readyState === 4 && xhr.status === 200) {
        if (end >= size - 1) {
          const response = JSON.parse(xhr.responseText);
          if (handleUpload) {
            handleUpload(response.secure_url);
          }
          return response.secure_url;
        }
        const nextStart = end + 1;
        const nextEnd = Math.min(nextStart + CLOUDINARY_FILE_CHUNK_SIZE, size);
        await uploadChunk(getChunk(nextStart, nextEnd), nextStart, nextEnd - 1, filename);
      }
    };

    form.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UNSIGNED_UPLOAD_PRESET);
    form.append('tags', 'thryve_admin_upload');
    form.append('file', chunk);
    form.append('public_id', filename);
    form.append('folder', process.env.REACT_APP_CLOUDINARY_FOLDER);
    xhr.send(form);
  };

  await uploadChunk(
    getChunk(0, CLOUDINARY_FILE_CHUNK_SIZE),
    0,
    CLOUDINARY_FILE_CHUNK_SIZE - 1,
    uniqueFilename(file.name),
  );
};

export const handleCloudinaryUpload = async (file, handleUpload, handleProgress) => {
  if (file.size >= CLOUDINARY_FILE_CHUNK_SIZE) {
    return handleCloudinaryLargeUpload(file, handleUpload, handleProgress);
  }

  const url = `${process.env.REACT_APP_CLOUDINARY_API_URL}/upload`;
  const xhr = new XMLHttpRequest();
  const form = new FormData();
  xhr.open('POST', url, true);
  xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

  if (handleProgress) {
    // progress can be used to show progress indicator in the ui
    xhr.upload.addEventListener('progress', (e) => {
      handleProgress(Math.round((e.loaded / e.total) * 100.0));
    });
  }

  xhr.onreadystatechange = () => {
    if (xhr.readyState === 4 && xhr.status === 200) {
      const response = JSON.parse(xhr.responseText);
      if (handleUpload) {
        handleUpload(response.secure_url);
      }
      return response.secure_url;
    }
  };

  form.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UNSIGNED_UPLOAD_PRESET);
  form.append('tags', 'thryve_admin_upload');
  form.append('file', file);
  form.append('public_id', uniqueFilename(file.name));
  form.append('folder', process.env.REACT_APP_CLOUDINARY_FOLDER);
  xhr.send(form);
};

const getResponseMessages = (arr: ResponseMsg[]): string[] => {
  return arr?.map((a) => (isString(a) ? a : a.message).trim()).filter(Boolean) || [];
};

export default function request<T = any>(options: RequestObj): AppThunk<Promise<T>> {
  return async (dispatch, getState) => {
    let finalUrl = replaceUrlParam(options.url, options.pathParams);
    const params = await buildParams(options, getState);
    finalUrl = `${API_BASE_URL}${finalUrl}?${qs.stringify(params)}`;
    const newObj: RequestInit = {
      headers: options.headers ? { ...DEFAULT_HEADERS, ...options.headers } : DEFAULT_HEADERS,
      method: options.method.toUpperCase(),
      credentials: 'include',
    };
    if (options.method !== 'get') {
      newObj.body = options.stringify === false ? options.data : JSON.stringify(options.data);
    }

    dispatch({ type: REDUX_ACTIONS.REQUEST_FETCH, url: finalUrl, method: newObj.method, body: options.data });
    try {
      const response = await fetch(finalUrl, newObj);
      if (!response.ok) {
        const errorResponseJson = await response.json();
        console.error('error response', response, errorResponseJson);
        let message = errorResponseJson.message;
        // Get the messages from the sharedTypes
        if (!message && isArray(errorResponseJson.errors) && errorResponseJson.errors.length > 0) {
          message = getResponseMessages(errorResponseJson.errors)[0];
        }
        // Got valid JSON with error response, use it
        if (
          [
            'You have been logged out due to inactivity.',
            'Unable to validate token.',
            'User not found, you will be logged out.',
          ].includes(message)
        ) {
          dispatch(logoutAction());
          // Make sure we close any open modals
          dispatch(closeModal());
        }
        throw new Error(message || response.status);
      }
      if (options.blob) {
        return await response.blob();
      }
      // Successful response, parse the JSON and return the data
      const jsonResult = await response.json();
      dispatch({ type: REDUX_ACTIONS.REQUEST_SUCCESS, url: finalUrl, method: newObj.method, result: jsonResult });
      return jsonResult;
    } catch (e) {
      console.error('fetch error', e);
      dispatch({ type: REDUX_ACTIONS.REQUEST_FAIL, url: finalUrl, method: newObj.method, error: e });
      if (
        (e.message || '').trim().toLowerCase() === 'failed to fetch' ||
        (e.message || '').trim().toLowerCase() === 'networkerror when attempting to fetch resource.' // firefox & safari
      ) {
        console.error('failed to fetch error', e);
      } else if (e.message.trim().toLowerCase() === 'sorry, you are not authorized for this request') {
        // suppress the toast if the user does not have permissions
        if (options.showUnauthorizedToast) {
          toast.error('Sorry, you do not have permission to do this.');
        }
        return null;
      } else {
        const shouldFilterError = options.errorToastFilters?.some((filter) => (e.message as string)?.includes(filter));
        if (!shouldFilterError) {
          toast.error(e.message);
        }
      }

      // Couldn't parse the JSON
      throw e;
    }
  };
}

export function updateDeviceData(): AppThunkP {
  return async (dispatch) => {
    if (IS_LOCAL) return;

    const requestExternal: RequestInit = {
      method: 'GET',
    };

    let ipResult: any = {};

    try {
      const response = await fetch('https://geolocation-db.com/json/', requestExternal);
      if (response.ok) {
        ipResult = await response.json();

        if (!ipResult) {
          return;
        }

        ipResult = {
          device_country_code: ipResult.country_code,
          device_country_name: ipResult.country_name,
          device_city: ipResult.city,
          device_postal: ipResult.postal,
          device_state: ipResult.state,
          user_agent: window.navigator.userAgent,
        };
      }
    } catch (error) {
      console.error('error getting user ip', error);
    }
    dispatch({ type: REDUX_ACTIONS.UPDATE_DEVICE_DATA, ...ipResult });
  };
}
